import { Alert, AlertTitle, Box, Typography } from '@mui/material';

interface DateChangeWarningContentProps {
  removeUnitCrewEnabled: boolean;
  resetStatusEnabled: boolean;
  clearNotifiedAcknowledgedEnabled: boolean;
}

type DateChangeRule = {
  enabled: boolean;
  singleRuleTitle: string;
  singleRuleDescription: string;
  bulletText: string;
};

const buildDateChangeRules = ({
  removeUnitCrewEnabled,
  resetStatusEnabled,
  clearNotifiedAcknowledgedEnabled
}: DateChangeWarningContentProps): DateChangeRule[] => [
  {
    enabled: removeUnitCrewEnabled,
    singleRuleTitle: 'Crew & Equipment Will Be Cleared',
    singleRuleDescription: 'Changing the job date will remove all currently assigned crew and equipment from this job ticket.',
    bulletText: 'All currently assigned crew and equipment will be removed'
  },
  {
    enabled: resetStatusEnabled,
    singleRuleTitle: 'Job Status Will Be Reset',
    singleRuleDescription: 'Changing the job date will reset the job status to the default status.',
    bulletText: 'The job status will be reset to the default status'
  },
  {
    enabled: clearNotifiedAcknowledgedEnabled,
    singleRuleTitle: 'Notification Status Will Be Cleared',
    singleRuleDescription: 'Changing the job date will clear the notification and acknowledgement statuses of all assigned crew members.',
    bulletText: 'Crew notification and acknowledgement statuses will be cleared'
  }
];

const renderAlertContent = (rules: DateChangeRule[]) => {
  const activeRules = rules.filter((rule) => rule.enabled);
  const hasOnlyOneActiveRule = activeRules.length === 1;
  const hasNoActiveRules = activeRules.length === 0;

  if (hasNoActiveRules) {
    return (
      <>
        <AlertTitle>Changing the job date may have side effects</AlertTitle>
        Depending on your configuration, this may affect job status, crew assignments, or notification statuses.
      </>
    );
  }

  if (hasOnlyOneActiveRule) {
    const [rule] = activeRules;
    return (
      <>
        <AlertTitle>{rule?.singleRuleTitle}</AlertTitle>
        {rule?.singleRuleDescription}
      </>
    );
  }

  return (
    <>
      <AlertTitle>The following changes will occur</AlertTitle>
      <Box component="ul" sx={{ pl: 2, m: 0 }}>
        {activeRules.map((rule, index) => (
          <li key={index}>{rule.bulletText}</li>
        ))}
      </Box>
    </>
  );
};

export const DateChangeWarningContent = (props: DateChangeWarningContentProps) => (
  <Box sx={{ pt: 2 }}>
    <Alert severity="warning">{renderAlertContent(buildDateChangeRules(props))}</Alert>
    <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5 }}>
      This action cannot be undone.
    </Typography>
  </Box>
);
